import { useState } from "react";
import { Camera, Printer, Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Button } from "../ui/button";
import { api, API } from "../../lib/api";
import { ALL_SHAPES, ShapeIcon } from "../child/shapes/shapeLibrary";

const NAME_BY_ID = Object.fromEntries(ALL_SHAPES.map((s) => [s.id, s.name]));

// Parent-facing Shape Photo Album: the child's real-world shape-hunt snaps
// with the shape each one matched (COPPA: family-gated, served through an
// authenticated backend endpoint, never public).
export const ShapeAlbum = ({ child }) => {
  const [photos, setPhotos] = useState(null);

  const load = () =>
    api.get(`/shapes/${child.child_id}/photos`)
      .then(({ data }) => setPhotos(data.photos || []))
      .catch(() => setPhotos([]));

  // Keepsake page: opens a clean print-ready page of the whole album in a new
  // tab and triggers the browser's print dialog (save as PDF or print).
  const printAlbum = () => {
    if (!photos?.length) return;
    const w = window.open("", "_blank");
    if (!w) return;
    const cells = photos.map((p) => `
      <figure style="margin:0;border:2px solid #E2E8F0;border-radius:12px;overflow:hidden;
        break-inside:avoid;background:#fff">
        <img src="${API}/shapes/${child.child_id}/photo/${p.photo_id}"
          style="width:100%;height:150px;object-fit:cover;display:block" />
        <figcaption style="padding:8px;font-size:12px;font-weight:700;color:#334155;
          display:flex;justify-content:space-between">
          <span>${NAME_BY_ID[p.shape_id] || p.shape_id}</span>
          <span style="color:#94A3B8">${p.day}${p.bonus ? " &#11088;" : ""}</span>
        </figcaption>
      </figure>`).join("");
    w.document.write(`<!doctype html><html><head><meta charset="utf-8">
      <title>${child.display_name}'s Shape Hunt Keepsake</title></head>
      <body style="font-family:sans-serif;padding:24px;max-width:800px;margin:0 auto">
      <h1 style="color:#0B1026;margin:0 0 4px">${child.display_name}'s Shape Hunt Keepsake</h1>
      <p style="color:#64748B;margin:0 0 20px;font-size:14px">
        ${photos.length} real-world shape${photos.length === 1 ? "" : "s"} found by
        ${child.display_name} &mdash; M Speech, Language &amp; Math Helper</p>
      <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:14px">${cells}</div>
      <script>window.onload=function(){setTimeout(function(){window.print()},700)}<\/script>
      </body></html>`);
    w.document.close();
  };

  return (
    <Dialog onOpenChange={(open) => open && load()}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full h-11 rounded-xl gap-2 text-sm"
          data-testid={`shape-album-button-${child.child_id}`}>
          <Camera className="h-4 w-4" aria-hidden="true" /> Shape Photo Album
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{child.display_name}'s Shape Hunt Album</DialogTitle>
        </DialogHeader>
        {photos === null && <p className="text-sm text-muted-foreground">Loading the album…</p>}
        {photos?.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="shape-album-empty">
            No shape-hunt photos yet — {child.display_name} can snap real-world shapes
            from the Shape Gallery in child mode to fill this album!
          </p>
        )}
        {photos?.length > 0 && (
          <>
            <Button variant="outline" onClick={printAlbum}
              className="w-full h-10 rounded-xl gap-2 text-sm"
              data-testid="shape-album-print">
              <Printer className="h-4 w-4" aria-hidden="true" /> Print keepsake page
            </Button>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="shape-album-grid">
            {photos.map((p) => (
              <figure key={p.photo_id} className="rounded-xl border border-border overflow-hidden bg-card"
                data-testid={`shape-album-photo-${p.photo_id}`}>
                <img src={`${API}/shapes/${child.child_id}/photo/${p.photo_id}`}
                  alt={`${NAME_BY_ID[p.shape_id] || p.shape_id} hunt photo`}
                  className="w-full h-32 object-cover" loading="lazy" />
                <figcaption className="p-2 flex items-center justify-between gap-1 text-xs font-semibold">
                  <span className="flex items-center gap-1.5 min-w-0">
                    <ShapeIcon shape={p.shape_id} className="h-4 w-4 shrink-0 text-secondary" />
                    <span className="truncate">{NAME_BY_ID[p.shape_id] || p.shape_id}</span>
                  </span>
                  <span className="flex items-center text-muted-foreground shrink-0">
                    {p.day}
                    {p.bonus && <Star className="h-3 w-3 ml-1 fill-amber-400 text-amber-500" aria-hidden="true" />}
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
